import React, { useMemo, useState, useCallback } from "react";
import * as Icons from "react-feather";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg';
import { formatDate, getStorageValues } from "../../shared/CommonFunctions";
import { getApiBaseUrl } from "../../utils/apiConfig";
import { logAuditEvent } from '../../utils/auditLogger';

function SalesRegisterGrid({ salesRegisters = [], onView, onEdit, onAdd, onDelete, UserPermissions = {}, moduleName = 'Sales Register' }) {
  const [filterText, setFilterText] = useState("");

  const handlePrintSalesBill = useCallback(async (row) => {
    const id = row?.SalesRegisterId;
    if (!id) return;

    try {
      const userId = getStorageValues("userId");
      const companyId = process.env.REACT_APP_COMPANY_ID || "1";
      const pdfUrl = `${getApiBaseUrl()}/salesregister/bill/pdf?SalesRegisterId=${encodeURIComponent(
        Number(id)
      )}&UserId=${encodeURIComponent(userId || "")}&CompanyId=${encodeURIComponent(companyId)}`;
      window.open(pdfUrl, "_blank");
    } catch (e) {
      console.error("Failed to generate Sales Bill PDF", e);
      // fallback to old behavior if provided
      try {
        onView && onView(row);
      } catch (err) {}
    }
  }, [onView]);

  const rows = useMemo(() => {
    const term = filterText.toLowerCase();
    return (salesRegisters || []).filter((item) => {
      return (
        (item.CustomerName && item.CustomerName.toLowerCase().includes(term)) ||
        (item.MobileNumber && item.MobileNumber.toLowerCase().includes(term)) ||
        (item.EnquireFor && item.EnquireFor.toLowerCase().includes(term))
      );
    });
  }, [salesRegisters, filterText]);

  const columns = [
    {
      name:<div className="titlename"><span>Sales#</span></div> ,
      selector: row => row.SalesNumber,
      cell: (row) => (
        <span title={row.SalesNumber}>{row.SalesNumber}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />,
      width: '160px'
    },
    {
      name:<div className="titlename"><span>Customer Name </span></div> ,
      selector: row => row.CustomerName,
      cell: (row) => (
        <span title={row.CustomerName}>{row.CustomerName}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Mobile Number </span></div>,
      selector: row => row.MobileNumber,
      cell: (row) => (
        <span title={row.MobileNumber}>{row.MobileNumber}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Vehicle</span></div>,
      selector: row => row.EnquireFor,
      cell: (row) => (
        <span title={row.EnquireFor}>{row.EnquireFor}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Sales Date </span></div>,
      selector: row => row.SalesDate,
      cell: (row) => (
        <span title={formatDate(row.SalesDate)}>{formatDate(row.SalesDate)}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
  ];

  const canView = Number(UserPermissions?.ViewModule) === 1;
  const canEdit = Number(UserPermissions?.EditModule) === 1;
  const canDelete = Number(UserPermissions?.DeleteModule) === 1;

  if (canView || canEdit || canDelete) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {canView && (
            <Link
              className="me-2 p-2"
              to=""
              onClick={() => {
                try {
                  const details = JSON.stringify({ SalesRegisterId: row?.SalesRegisterId, CustomerName: row?.CustomerName });
                  logAuditEvent(moduleName, 'View Button Click', details);
                } catch (e) {}
                handlePrintSalesBill(row);
              }}
            >
              <Icons.Printer data-feather="printer" size={14} className="feather-printer" />
            </Link>
          )}
          {canView && (
            <div className="dropdown d-inline-block">
              <Link
                className="me-2 p-2"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                title="Send"
                onClick={(e) => e.preventDefault()}
              >
                <Icons.Mail size={14} data-feather="mail" className="feather-mail" />
              </Link>
              <div className="dropdown-menu notes-menu dropdown-menu-end" onClick={(e) => e.stopPropagation()}>
                <Link
                  className="dropdown-item d-flex align-items-center text-start"
                  to=""
                  onClick={(e) => {
                    e.preventDefault();
                    try {
                      const details = JSON.stringify({ SalesRegisterId: row?.SalesRegisterId, CustomerName: row?.CustomerName, Action: 'Email' });
                      logAuditEvent(moduleName, 'Email Option Click', details);
                    } catch (e) {}
                  }}
                >
                  <Icons.Mail size={14} className="me-2" />
                  Email
                </Link>
                <Link
                  className="dropdown-item d-flex align-items-center text-start"
                  to=""
                  onClick={(e) => {
                    e.preventDefault();
                    try {
                      const details = JSON.stringify({ SalesRegisterId: row?.SalesRegisterId, CustomerName: row?.CustomerName, Action: 'WhatsApp' });
                      logAuditEvent(moduleName, 'WhatsApp Option Click', details);
                    } catch (e) {}
                  }}
                >
                  <Icons.MessageCircle size={14} className="me-2" />
                  WhatsApp
                </Link>
              </div>
            </div>
          )}
          {canEdit && (
            <Link
              className="me-2 p-2"
              to=""
              onClick={() => {
                try {
                  const details = JSON.stringify({ SalesRegisterId: row?.SalesRegisterId, CustomerName: row?.CustomerName });
                  logAuditEvent(moduleName, 'Edit Button Click', details);
                } catch (e) {}
                onEdit && onEdit(row);
              }}
            >
              <Icons.Edit data-feather="edit" className="feather-edit" />
            </Link>
          )}
          {canDelete && (
            <Link
              className="confirm-text p-2"
              to=""
              onClick={(e) => {
                e.preventDefault();
                try {
                  const details = JSON.stringify({ SalesRegisterId: row?.SalesRegisterId, CustomerName: row?.CustomerName });
                  logAuditEvent(moduleName, 'Delete Button Click', details);
                } catch (e) {}
                onDelete && onDelete(row);
              }}
            >
              <Icons.Trash2 data-feather="trash-2" className="feather-trash-2" />
            </Link>
          )}
        </div>
      ),
      center: "true",
      width: "160px"
    });
  }

  return (
    <>
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="CustomerName"
            sortIcon={<SortArrow/>}
            persistTableHead={true}
          />
        </div>
      </div>
    </>
  );
}

export default SalesRegisterGrid;
